import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ArtworkMedium } from "../lib/artworkPaths";
import { artworkPagePath, galleryPathForMedium } from "../lib/artworkPaths";
import { relatedArtworksFor } from "../lib/relatedArtworks";
import { CanvasGalleryCard } from "./CanvasArtworkCards";
import { AiGalleryCard } from "./AiArtworkCards";
import { DigitalGalleryCard } from "./DigitalArtworkCards";

type RelatedArtworksSectionProps = {
  medium: ArtworkMedium;
  currentId: string;
  collection: string;
  collectionLabel: string;
};

export function RelatedArtworksSection({
  medium,
  currentId,
  collection,
  collectionLabel,
}: RelatedArtworksSectionProps) {
  const { t } = useTranslation();
  const related = useMemo(
    () => relatedArtworksFor(medium, collection, currentId),
    [medium, collection, currentId],
  );

  if (related.length === 0) return null;

  return (
    <section className="border-t border-canvas-200/60 bg-canvas-50/40">
      <div className="section-container py-12 sm:py-16 lg:py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-accent">
            {t("artwork.relatedEyebrow")}
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-canvas-950 sm:text-3xl">
            {t("artwork.relatedTitle")}
          </h2>
          <p className="mt-2 text-sm text-canvas-600 sm:text-base">
            {t("artwork.relatedSubtitle", { collection: collectionLabel })}
          </p>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {related.map((item) => {
            if (item.medium === "canvas") {
              return (
                <CanvasGalleryCard
                  key={item.work.id}
                  work={item.work}
                  to={artworkPagePath("canvas", item.work.id)}
                  className="mb-0"
                />
              );
            }
            if (item.medium === "ai") {
              return (
                <AiGalleryCard
                  key={item.work.id}
                  work={item.work}
                  to={artworkPagePath("ai", item.work.id)}
                  className="mb-0"
                />
              );
            }
            return (
              <DigitalGalleryCard
                key={item.work.id}
                work={item.work}
                to={artworkPagePath("digital", item.work.id)}
                className="mb-0"
              />
            );
          })}
        </div>

        <div className="mt-8 flex justify-center sm:mt-10">
          <Link
            to={galleryPathForMedium(medium, collection)}
            className="btn-secondary !px-5 !py-2.5 !text-sm"
          >
            {t("artwork.viewCollection", { collection: collectionLabel })}
          </Link>
        </div>
      </div>
    </section>
  );
}
